import { Fragment, memo, useCallback } from 'react';
import type { FC } from 'react';
import BuyButton from '@components/BuyButton';
import type { GetRecipeByIdQuery } from '@schema/cart';
import { Link, useHistory } from '@compat/router';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import RecipeAddAllToCart from '../RecipeAddAllToCart';
import { twcls } from '@utils/twcls';
import { useStores } from '@root/src/stores';

interface RecipeIngredientsProps {
  ingredients: NonNullable<
    GetRecipeByIdQuery['getRecipeById']
  >['recipeIngredients'];
  isDesktop?: boolean;
  isTablet?: boolean;
}
const RecipeIngredients: FC<RecipeIngredientsProps> = ({
  ingredients,
  isDesktop,
  isTablet
}) => {
  const history = useHistory();
  const { modalsStore } = useStores();

  const openQuickInfoModal = useCallback(
    (id: string): void => {
      modalsStore.openQuickInfo(id);
    },
    [modalsStore]
  );

  const TitleWrapper = useCallback(
    ({ catalogItem, children }) => {
      if (isDesktop) {
        return (
          <div
            onClick={() => {
              if (!catalogItem?.id) {
                return;
              }
              openQuickInfoModal(catalogItem.id);
            }}
            className='flex items-center  underline transition-colors hover:text-[var(--dark-red)] '
          >
            {children}
          </div>
        );
      }

      return (
        <Link
          target='_blank'
          className='flex items-center  underline transition-colors hover:text-[var(--dark-red)] '
          to={`/product/${catalogItem.id}`}
        >
          {children}
        </Link>
      );
    },
    [isDesktop, openQuickInfoModal]
  );

  if (!ingredients?.length) return null;
  return (
    <>
      <div
        className={twcls(`sticky
        min-h-[22.625rem]
        allMd:min-h-0
        flex
        flex-col
        top-[139px]
        allMd:mb-0
        allMd:relative
        allMd:top-0
        left-0
        p-4
        allMd:rounded-none
        allMd:bg-white
        allMd:p-0
        rounded-2xl
        bg-[var(--light-grey)]`)}
      >
        <h2 className='mb-6 text-xl allMd:hidden'>Ингредиенты</h2>
        {!!ingredients?.length && (
          <ul className='mb-6'>
            {ingredients.map((item, index) => {
              const catalogItem = item?.ingredientsItems?.[0]?.catalogItem;
              const image = catalogItem?.fileItem?.cacheItem?.[0]?.filePath;
              const price2Obj = catalogItem?.currentPriceField2
                ? preparePriceFloatObject(catalogItem.currentPriceField2)
                : null;
              return (
                <Fragment key={`ingredient-desktop-'${item.id}`}>
                  <li
                    data-testid='recipe-ingredient'
                    className={twcls(
                      `
                    border-[var(--mid-grey)]
                    py-3
                    grid
                    grid-cols-[320px,90px,120px]
                    sm:flex
                    sm:justify-between
                    sm:gap-4
                    sm:gap-y-1
                    sm:flex-nowrap
                    sm:items-center
                    justify-between
                    text-sm`,
                      index !== ingredients.length - 1 &&
                        'border-solid border-t-0 border-l-0 border-r-0 border-b'
                    )}
                  >
                    <div className='flex items-center justify-between text-sm sm:items-center sm:w-full'>
                      {catalogItem?.id ? (
                        <div>
                          <TitleWrapper catalogItem={catalogItem}>
                            {image && (
                              <div className='mr-3 overflow-hidden rounded-lg cursor-pointer w-14 h-14'>
                                <img
                                  src={image}
                                  width={56}
                                  height={56}
                                  alt={catalogItem.id}
                                  className='object-contain w-full h-full'
                                />
                              </div>
                            )}
                            <div>
                              {item?.title?.includes('http') ? (
                                <span className='flex items-center'>
                                  {item.title.split('>')[1].split('<')[0]}
                                  <div
                                    onClick={e => {
                                      e.preventDefault();
                                      history.push(
                                        item?.title
                                          ?.split('"')?.[1]
                                          ?.split(
                                            'https://www.delikateska.ru'
                                          )?.[1] ?? '/'
                                      );
                                    }}
                                  >
                                    Наш рецепт
                                  </div>
                                </span>
                              ) : (
                                <>
                                  <span className='flex items-center cursor-pointer'>
                                    {item.title}
                                  </span>
                                  <div className='inline-block fromMd:hidden'>
                                    <span className='text-base font-medium'>
                                      {price2Obj?.wholePart}
                                      {price2Obj?.floatPart && (
                                        <span className='text-xs'>
                                          ,{price2Obj?.floatPart}
                                        </span>
                                      )}{' '}
                                      <span className='text-xs'>₽</span>
                                    </span>
                                    <span className='inline-block'>/</span>
                                    <span className='text-[var(--ultra-dark-grey)] text-sm'>
                                      {catalogItem?.symbolPriceField2?.slice(8)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </TitleWrapper>
                        </div>
                      ) : (
                        <>
                          <div className='no-links'>
                            <div className='item-title'>
                              {item?.title?.includes('<a') ? (
                                <span className='flex items-center'>
                                  {item?.title?.split('>')[1].split('<')[0]}
                                  &nbsp;
                                  <Link
                                    to={
                                      item?.title?.split('"')[1].split('"')[0]
                                    }
                                    className='our-recipe'
                                  >
                                    Наш рецепт
                                  </Link>
                                </span>
                              ) : (
                                <span className='flex items-center'>
                                  {item.title}
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <span className='flex items-center justify-center'>
                        {item.value}
                      </span>
                    </div>

                    {(isDesktop || isTablet) &&
                    item?.ingredientsItems?.length ? (
                      <div className='flex flex-col items-center justify-center sm:inline-block'>
                        <span className='text-base font-medium'>
                          {price2Obj?.wholePart}
                          {price2Obj?.floatPart && (
                            <span className='text-xs'>
                              ,{price2Obj?.floatPart}
                            </span>
                          )}{' '}
                          <span className='text-xs'>₽</span>
                        </span>
                        <span className='hidden sm:inline-block'>/</span>
                        <span className='text-[var(--ultra-dark-grey)] sm:text-sm'>
                          {item?.ingredientsItems?.[0]?.catalogItem?.symbolPriceField2?.slice(
                            8
                          )}
                        </span>
                      </div>
                    ) : null}

                    {item?.ingredientsItems?.[0]?.catalogItem?.id && (
                      <BuyButton
                        canBeBought
                        isWhite
                        available={
                          item.ingredientsItems[0].catalogItem?.available ||
                          false
                        }
                        onlyPlusButton
                        productId={+item.ingredientsItems[0].catalogItem.id}
                        style={
                          item.ingredientsItems[0].catalogItem.available
                            ? {}
                            : { visibility: 'hidden' }
                        }
                      />
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        )}
        {isDesktop && (
          <div className='hidden mt-auto fromLg:block'>
            <RecipeAddAllToCart
              isDesktop={isDesktop}
              catalogItemsIds={
                ingredients
                  ?.filter(
                    item =>
                      item.ingredientsItems?.[0]?.catalogItem?.available &&
                      item?.ingredientsItems?.[0]?.catalogItem?.id
                  )
                  ?.map(
                    item =>
                      item?.ingredientsItems?.[0]?.catalogItem?.id as string
                  ) || []
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(RecipeIngredients);
